import type { KpiSignal } from "./types";
import { toTime } from "./timeSeries";
import { KPI_DEFAULTS } from "./defaults";
// Seed data import removed — targets come exclusively from the API via projectTargets param.
// import { missionTargets as seedMissionTargets } from "../Data/KpiEngine/missionTargets";
import type { MissionValueType } from "../Data/KpiEngine/missionOutcomes";
import {
  assessmentToColorValue,
  assessmentToScore,
  isQualitativeAssessment,
  type QualitativeAssessment,
} from "./qualitativeLadder";

// ── Public Types ─────────────────────────────────────────────

export type MissionMetricPoint = {
  project_GUID: string;
  subKpiKey: string;
  date: string;   // YYYY-MM-DD
  value: number;
  entered_at?: string;
  /** For milestone_set value type: total milestones in the set */
  milestone_total?: number;
  /**
   * Optional Checklist item identifier (DEV-816 Option D).
   * When present, this point is the completion event for a SPECIFIC item
   * in a Checklist sub-KPI. The engine groups these per-item to derive
   * which items are complete as of any date. Null/undefined for non-
   * Checklist points and legacy Checklist data (which falls back to
   * first-N-in-order rendering).
   */
  item_id?: string;
  /**
   * Optional qualitative assessment (DEV-1075 Path A). One of
   * 'ahead' | 'on_track' | 'behind' | 'blocked' | 'done'. Populated on
   * Mini-Project rows (where the Lead picks a state per goal at every
   * Status update) and ignored on Full-Project rows (which use the
   * numeric value field). The engine routes to assessmentToScore /
   * assessmentToColorValue when the parent sub-KPI's config sets
   * scoring_mode='qualitative'.
   */
  assessment?: string;
};

export type MissionSubKpiConfig = {
  weight: number;
  cadence?: "daily" | "weekly" | "monthly" | "once";
  permissible_days_late?: number;
  direction?: "increase_is_good" | "decrease_is_good" | "target_is_good";
  /** Value type for scoring. Defaults to "numeric" for backward compat. */
  value_type?: MissionValueType;
  /** Optional outcome_id reference to the catalog */
  outcome_id?: string;
  /** Aggregation mode for data points */
  aggregation?: "cumulative" | "periodic";
  /** How to handle missing data */
  missing_policy?: "zero" | "missing";
  /** Extra eval config (milestone freeze, etc.) */
  evaluation?: Record<string, any>;
  /** Human-readable label */
  label?: string;
  /** Date when tracking begins for this sub-KPI (YYYY-MM-DD). Defaults to project start date. */
  tracking_start_date?: string;
  /** Deadline date for this sub-KPI (YYYY-MM-DD). Scores degrade as the deadline approaches without achievement. */
  deadline_date?: string;
  /** Days before deadline to trigger Yellow warning (e.g. 30 → score drops to 80). Default: 30. */
  warning_days?: number;
  /** Days before deadline to trigger Red critical (e.g. 7 → score drops to 70). Default: 7. */
  critical_days?: number;
  /** Unit of measure (passed through for display). */
  uom?: string;
  /** Target goal value (passed through for display). */
  target_goal?: number;
  /** Color derivation mode (DEV-814 / migration 122; 'pace' added DEV-815).
   *  'score'    = color from progress vs bands (default for non-Binary).
   *  'deadline' = color from time remaining; score derived from color.
   *  'pace'     = color from variance against expected-by-today.
   *  Undefined = use KPI_DEFAULTS per type. */
  color_mode?: 'score' | 'deadline' | 'pace';
  /** Pace-mode tolerance (DEV-815): yellow when behind expected by >= this. */
  pace_yellow_units_behind?: number;
  /** Pace-mode tolerance (DEV-815): red when behind expected by >= this. */
  pace_red_units_behind?: number;
  /** Optional tags for categorization and filtering (e.g. ["environmental", "community"]). */
  tags?: string[];
  /**
   * Optional snapshot of the new-architecture mission_type_config object
   * (DEV-816 / Skip). Lets the card surface type-specific detail (e.g.,
   * the milestone labels for Milestone Set) without re-fetching the raw
   * instance. Engine echoes this through to the result; engine doesn't
   * use it for scoring (legacy fields still drive that, see
   * bridgeToLegacyFields in MissionKpiWizard).
   */
  mission_type_config?: Record<string, unknown>;
  /**
   * Mission sub-KPI type discriminator (DEV-822). Identifies which
   * registered MissionSubKpiType this instance uses. Echoed through
   * to MissionSubKpiResult so the UI can branch its render — most
   * notably to extract HSE-typed sub-KPIs (incident_count) and render
   * them in the HSERow at the top of the Mission tab instead of as a
   * regular Mission row. Engine itself doesn't dispatch on this in the
   * legacy scoring path (HSE scoring runs in parallel via hse.ts).
   */
  mission_type_key?: string;
  /**
   * Scoring mode (DEV-1075 Path A). 'numeric' (default) keeps the
   * existing value-vs-threshold path that scoreNumeric et al. drive.
   * 'qualitative' routes through the 5-state assessment ladder
   * (qualitativeLadder.ts): the latest point's .assessment field
   * supplies score + color, and the numeric / deadline / band /
   * milestone pipeline is bypassed entirely. Used by Mini-Project
   * mission sub-KPIs (the qualitative ownership tier).
   */
  scoring_mode?: 'numeric' | 'qualitative';
  /**
   * DEV-1075. When false, this subKPI's color is still computed and
   * rendered on its own card, but it's excluded from the Mission
   * rollup's weighted-average score. Replaces the weight=0 workaround
   * for administrative metrics that shouldn't influence project health.
   * Default true (counts toward rollup).
   */
  included_in_rollup?: boolean;
};

export type MissionConfig = {
  subkpis: Record<string, MissionSubKpiConfig>;
};

export type TargetDef = {
  goal?: number;
  baseline?: number;
  direction?: "increase_is_good" | "decrease_is_good" | "target_is_good";
  /** For milestone_set: total items in the set */
  milestone_total?: number;
};

/** Result for a single sub-KPI evaluation */
export type MissionSubKpiResult = {
  subKpiKey: string;
  /** Human-readable label from the configured display_name (e.g. "Facility Operational") */
  label?: string;
  /** Health score, capped 0–100. Null = tracked-only. */
  score_0_100: number | null;
  /**
   * DEV-864: Raw progress 0–100 with NO deadline penalty.
   * Drives the MissionSubKpiCard progress-bar fill so it tracks user input
   * proportionally. Distinct from score_0_100 (which includes deadline
   * degradation). Null when type doesn't have a meaningful raw progress
   * (e.g., binary uses a badge) or when no data is available.
   */
  progress_0_100: number | null;
  /** Uncapped achievement ratio (actual/target × 100). Null = not scored. */
  achievement_ratio: number | null;
  indicator_color_value: number;
  indicator_trend_value: -1 | 0 | 1;
  /** The weight that actually contributed to the composite score (0 when tracked-only). */
  weight_used: number;
  /** The configured weight from KPI Configuration (always reflects what the user set). */
  weight_configured: number;
  tracked_only: boolean;
  latest_date: string | null;
  latest_value: number | null;
  explain: string;
  /** Value type used for scoring */
  value_type: MissionValueType;
  /** Outcome catalog reference (if available) */
  outcome_id?: string;
  /** Date when tracking begins (YYYY-MM-DD). Defaults to project start date. */
  tracking_start_date?: string | null;
  /** Deadline date (YYYY-MM-DD). Scores degrade as the deadline approaches without achievement. */
  deadline_date?: string | null;
  /** Days before deadline that triggers Yellow warning (score → 80). */
  warning_days?: number;
  /** Days before deadline that triggers Red critical (score → 70). */
  critical_days?: number;
  /** Unit of measure (passed through from config for display). */
  uom?: string;
  /** Target goal value (passed through for display: "current / target"). */
  target_goal?: number | null;
  /** Tags for categorization and filtering (passed through from config). */
  tags?: string[];
  /**
   * Snapshot of the new-architecture mission_type_config (DEV-816 / Skip).
   * Echoed through from the input config for the card to render type-
   * specific detail like the Milestone Set milestone list with check marks.
   */
  mission_type_config?: Record<string, unknown>;
  /**
   * Mission sub-KPI type key (DEV-822). Echoed through from the input
   * config so the UI layer can identify HSE-typed sub-KPIs and route
   * them to the HSERow renderer instead of the regular Mission card list.
   */
  mission_type_key?: string;
};

// ── Internal Helpers ─────────────────────────────────────────

function clamp01(x: number): number {
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}

function asOfPoint<T>(points: T[], asOfDate: string, getDate: (p: T) => string): T | null {
  const target = toTime(asOfDate);
  let best: T | null = null;
  let bestT = -Infinity;
  for (const p of points) {
    const t = toTime(getDate(p));
    if (t <= target && t > bestT) {
      best = p;
      bestT = t;
    }
  }
  return best;
}

function previousPoint<T>(points: T[], asOfDate: string, getDate: (p: T) => string): T | null {
  const target = toTime(asOfDate);
  let best: T | null = null;
  let bestT = -Infinity;
  for (const p of points) {
    const t = toTime(getDate(p));
    if (t < target && t > bestT) {
      best = p;
      bestT = t;
    }
  }
  return best;
}

// ── Scoring Functions ────────────────────────────────────────

/**
 * Score a numeric sub-KPI (the original scoring path).
 * Returns both capped health score and uncapped achievement ratio.
 */
function scoreNumeric(args: {
  value: number;
  target: TargetDef;
  cfgDirection: MissionSubKpiConfig["direction"];
}): { score: number | null; progress: number | null; achievement_ratio: number | null; explain: string } {
  // DEV-864: progress = clamped 0-100 raw % toward target, no deadline penalty.
  // Equal to score BEFORE applyDeadlineScoring / applyColorMode* mutations.
  const { value, target, cfgDirection } = args;
  const direction = target.direction ?? cfgDirection ?? "increase_is_good";

  if (direction === "increase_is_good") {
    const goal = target.goal;
    if (goal === undefined || goal === null || goal <= 0) {
      return { score: null, progress: null, achievement_ratio: null, explain: "Tracked-only: no goal configured." };
    }
    const raw = value / goal;
    const clamped = clamp01(raw) * 100;
    return {
      score: clamped,
      progress: clamped,
      achievement_ratio: raw * 100,
      explain: `Scored vs goal (${value} / ${goal}).`,
    };
  }

  if (direction === "decrease_is_good") {
    const baseline = target.baseline;
    const goal = target.goal;
    if (baseline === undefined || goal === undefined || baseline === null || goal === null) {
      return { score: null, progress: null, achievement_ratio: null, explain: "Tracked-only: baseline and goal required for reduction scoring." };
    }
    const denom = baseline - goal;
    if (denom <= 0) {
      return { score: null, progress: null, achievement_ratio: null, explain: "Tracked-only: invalid reduction target (baseline must be > goal)." };
    }
    const raw = (baseline - value) / denom;
    const clamped = clamp01(raw) * 100;
    return {
      score: clamped,
      progress: clamped,
      achievement_ratio: raw * 100,
      explain: `Scored reduction (${baseline} → ${goal}).`,
    };
  }

  if (direction === "target_is_good") {
    const goal = target.goal;
    if (goal === undefined || goal === null) {
      return { score: null, progress: null, achievement_ratio: null, explain: "Tracked-only: no target configured." };
    }
    const dist = Math.abs(value - goal);
    const band = Math.max(1e-9, Math.abs(goal) * 2);
    const raw = 1 - dist / band;
    const clamped = clamp01(raw) * 100;
    return {
      score: clamped,
      progress: clamped,
      achievement_ratio: raw * 100,
      explain: `Scored closeness to target (${goal}).`,
    };
  }

  return { score: null, progress: null, achievement_ratio: null, explain: "Tracked-only: unsupported direction." };
}

/**
 * Score a binary sub-KPI (achievement: done or not).
 * value >= 1 means achieved → 100.
 *
 * When a deadline + tolerance thresholds are configured, unachieved
 * outcomes degrade based on proximity to the deadline:
 *   - Plenty of time remaining → 100 (Green — on track)
 *   - Within warning_days      →  80 (Yellow boundary)
 *   - Within critical_days     →  70 (Red-ish — urgent)
 *   - Overdue                  →   0 (hard fail)
 */
function scoreBinary(
  value: number,
  opts?: {
    deadline_date?: string | null;
    warning_days?: number;
    critical_days?: number;
    asOfDate?: string;
  }
): {
  score: number | null;
  progress: number | null;
  achievement_ratio: number | null;
  explain: string;
} {
  // DEV-864: Binary uses an achievement badge, not a progress bar.
  // progress is null across all branches.
  const achieved = value >= 1;
  if (achieved) {
    return {
      score: 100,
      progress: null,
      achievement_ratio: 100,
      explain: "Achievement completed.",
    };
  }

  // No deadline configured → simple binary 0/100
  if (!opts?.deadline_date) {
    return {
      score: 0,
      progress: null,
      achievement_ratio: 0,
      explain: "Achievement not yet reached.",
    };
  }

  // Deadline-based scoring for unachieved binary outcomes
  const deadlineMs = new Date(opts.deadline_date).getTime();
  const asOfMs = opts.asOfDate
    ? new Date(opts.asOfDate).getTime()
    : Date.now();
  const daysLeft = (deadlineMs - asOfMs) / 86_400_000;

  const warningDays = opts.warning_days ?? KPI_DEFAULTS.deadline.warning_days;
  const criticalDays = opts.critical_days ?? KPI_DEFAULTS.deadline.critical_days;

  if (daysLeft < 0) {
    return {
      score: KPI_DEFAULTS.deadline.overdue_score,
      progress: null,
      achievement_ratio: 0,
      explain: `Overdue — deadline was ${opts.deadline_date}.`,
    };
  }
  if (daysLeft <= criticalDays) {
    return {
      score: KPI_DEFAULTS.deadline.critical_score_cap,
      progress: null,
      achievement_ratio: 0,
      explain: `Critical — ${Math.ceil(daysLeft)}d until deadline ${opts.deadline_date}.`,
    };
  }
  if (daysLeft <= warningDays) {
    return {
      score: KPI_DEFAULTS.deadline.warning_score_cap,
      progress: null,
      achievement_ratio: 0,
      explain: `Warning — ${Math.ceil(daysLeft)}d until deadline ${opts.deadline_date}.`,
    };
  }

  // Plenty of time — on track
  return {
    score: KPI_DEFAULTS.mission.binary_score_levels.achieved,
    progress: null,
    achievement_ratio: 0,
    explain: `On track — deadline ${opts.deadline_date} (${Math.ceil(daysLeft)}d away).`,
  };
}

/**
 * Score a percentage sub-KPI (rate toward a target rate).
 * value is the actual percentage (0–100+), target.goal is the target percentage.
 */
function scorePercentage(
  value: number,
  target: TargetDef
): { score: number | null; progress: number | null; achievement_ratio: number | null; explain: string } {
  // DEV-864: progress = clamped raw % toward target, no deadline penalty.
  const goal = target.goal;
  if (goal === undefined || goal === null || goal <= 0) {
    return { score: null, progress: null, achievement_ratio: null, explain: "Tracked-only: no target rate configured." };
  }
  const raw = value / goal;
  const clamped = clamp01(raw) * 100;
  return {
    score: clamped,
    progress: clamped,
    achievement_ratio: raw * 100,
    explain: `Rate ${value}% vs target ${goal}%.`,
  };
}

/**
 * Score a milestone_set sub-KPI (N of M completed).
 * value is the count of completed milestones.
 * target.milestone_total or target.goal is the total count.
 */
function scoreMilestoneSet(
  value: number,
  target: TargetDef,
  point?: MissionMetricPoint | null
): { score: number | null; progress: number | null; achievement_ratio: number | null; explain: string } {
  // DEV-864: progress = (completed / total) * 100, no deadline penalty.
  const total = target.milestone_total ?? target.goal;
  if (total === undefined || total === null || total <= 0) {
    return { score: null, progress: null, achievement_ratio: null, explain: "Tracked-only: total milestones not configured." };
  }
  const completed = Math.min(value, total);
  const ratio = completed / total;
  const pct = ratio * 100;
  return {
    score: pct,
    progress: pct,
    achievement_ratio: pct,
    explain: `${completed} of ${total} milestones completed.`,
  };
}

/**
 * Apply deadline-proximity scoring to ANY value type whose goal has NOT been met.
 *
 * When a deadline is configured:
 *   - Goal met                     → 100  (Green — base score passthrough)
 *   - Plenty of time remaining     → 100  (Green — on track, goal not yet due)
 *   - Within warning_days          →  80  (Yellow — deadline approaching)
 *   - Within critical_days         →  70  (Red-ish — urgent)
 *   - Overdue                      →   0  (hard fail)
 *
 * When no deadline is configured, fall through to the existing type-specific
 * scorer (ratio-based for numeric/percentage/milestone, binary 0/100).
 */
function applyDeadlineScoring(
  baseResult: { score: number | null; progress: number | null; achievement_ratio: number | null; explain: string },
  goalMet: boolean,
  opts?: {
    deadline_date?: string | null;
    warning_days?: number;
    critical_days?: number;
    asOfDate?: string;
  }
): { score: number | null; progress: number | null; achievement_ratio: number | null; explain: string } {
  // DEV-864: progress is the raw % toward target (no deadline penalty).
  // It is preserved unchanged through every degradation branch so the
  // MissionSubKpiCard progress bar tracks user input proportionally.
  // If goal is already met, full marks regardless of deadline.
  if (goalMet) return baseResult;

  // No deadline configured → use base score unchanged.
  if (!opts?.deadline_date) return baseResult;

  const deadlineMs = new Date(opts.deadline_date).getTime();
  const asOfMs = opts.asOfDate
    ? new Date(opts.asOfDate).getTime()
    : Date.now();
  const daysLeft = (deadlineMs - asOfMs) / 86_400_000;

  const warningDays = opts.warning_days ?? KPI_DEFAULTS.deadline.warning_days;
  const criticalDays = opts.critical_days ?? KPI_DEFAULTS.deadline.critical_days;

  // Bug B1 fix (DEV-814 / Skip's "Interns Hired" report):
  //
  // Pre-fix this function REPLACED baseResult.score with the deadline cap
  // values (75 in warning, 50 in critical, 100 far from deadline). That made
  // every Mission KPI in the warning window read the same score regardless
  // of actual progress — Skip's count of interns hired stayed at 75/100 from
  // 0 hired through 5 hired (only flipping to 100 when goal was met, because
  // goalMet=true short-circuits this function).
  //
  // The correct semantics: deadline windows act as a CEILING, not a floor.
  //   - Far from deadline: use the progress-based score as-is.
  //   - In warning window: cap the score at warning_score_cap (75).
  //   - In critical window: cap at critical_score_cap (50).
  //   - Overdue: hard fail at 0.
  //
  // So a count at 5/6 (base 83) in the warning window scores min(83, 75) = 75.
  // A count at 1/6 (base 17) in the warning window scores min(17, 75) = 17.
  // A count at 5/6 far from deadline scores 83 (no cap applied).
  //
  // baseResult.score may be null (e.g., target not configured). In that case
  // we have no progress signal to cap, so fall through with a null score —
  // the user has more work to do before this KPI can be scored at all.

  const base = baseResult.score;
  if (base === null) {
    // Nothing to cap; preserve the tracked-only state. Don't synthesize a
    // misleading "deadline-driven" score from missing config.
    return baseResult;
  }

  if (daysLeft < 0) {
    return {
      score: KPI_DEFAULTS.deadline.overdue_score,
      progress: baseResult.progress, // DEV-864: progress unchanged by deadline penalty
      achievement_ratio: baseResult.achievement_ratio,
      explain: `Overdue — deadline was ${opts.deadline_date}. ${baseResult.explain}`,
    };
  }
  if (daysLeft <= criticalDays) {
    const capped = Math.min(base, KPI_DEFAULTS.deadline.critical_score_cap);
    return {
      score: capped,
      progress: baseResult.progress,
      achievement_ratio: baseResult.achievement_ratio,
      explain: `Critical — ${Math.ceil(daysLeft)}d until deadline ${opts.deadline_date}. ${baseResult.explain}`,
    };
  }
  if (daysLeft <= warningDays) {
    const capped = Math.min(base, KPI_DEFAULTS.deadline.warning_score_cap);
    return {
      score: capped,
      progress: baseResult.progress,
      achievement_ratio: baseResult.achievement_ratio,
      explain: `Warning — ${Math.ceil(daysLeft)}d until deadline ${opts.deadline_date}. ${baseResult.explain}`,
    };
  }

  // Plenty of time remaining — return the natural progress score with no
  // deadline cap. (Pre-fix this returned 100, hiding all progress signal.)
  return baseResult;
}

/**
 * Apply color_mode === 'deadline' scoring (DEV-814).
 *
 * In this mode the COLOR is driven by time-to-deadline (deadline windows)
 * and the SCORE used in the Mission weighted rollup is DERIVED from that
 * color via KPI_DEFAULTS.mission.color_mode_deadline_scores (100/75/50/50).
 *
 * Why: a project just starting out shouldn't be punished by a 0% progress
 * score when its deadline is months away. In 'deadline' mode the same KPI
 * scores 100 (green = far from deadline) and the Mission rollup stays
 * meaningful even when individual KPIs have minimal progress.
 *
 * Skip's intent (DEV-814): "It might not be fair for a project just starting
 * out to be penalized on the Mission score because they haven't reached a
 * goal immediately."
 *
 * Goal-met short-circuit applies first: if achieved, always green/100.
 * Otherwise, color (and therefore score) is purely time-based.
 */
function applyColorModeDeadlineScoring(
  baseResult: { score: number | null; progress: number | null; achievement_ratio: number | null; explain: string },
  goalMet: boolean,
  opts?: {
    deadline_date?: string | null;
    warning_days?: number;
    critical_days?: number;
    asOfDate?: string;
  }
): { score: number | null; progress: number | null; achievement_ratio: number | null; explain: string } {
  // DEV-864: progress passes through unchanged. score is fully replaced
  // by deadline-window values in this mode (color is the score), but the
  // bar fill still tracks raw progress.
  const scores = KPI_DEFAULTS.mission.color_mode_deadline_scores;
  // Goal met → always green/100, irrespective of timing.
  if (goalMet) {
    return {
      score: scores.green,
      progress: baseResult.progress,
      achievement_ratio: baseResult.achievement_ratio,
      explain: `Goal met. ${baseResult.explain}`,
    };
  }
  // No deadline → can't infer a time-based color. Fall back to the natural
  // base score so the user still sees something meaningful.
  if (!opts?.deadline_date) return baseResult;

  const deadlineMs = new Date(opts.deadline_date).getTime();
  const asOfMs = opts.asOfDate
    ? new Date(opts.asOfDate).getTime()
    : Date.now();
  const daysLeft = (deadlineMs - asOfMs) / 86_400_000;

  const warningDays = opts.warning_days ?? KPI_DEFAULTS.deadline.warning_days;
  const criticalDays = opts.critical_days ?? KPI_DEFAULTS.deadline.critical_days;

  if (daysLeft < 0) {
    return {
      score: scores.overdue,
      progress: baseResult.progress,
      achievement_ratio: baseResult.achievement_ratio,
      explain: `Overdue — deadline was ${opts.deadline_date}. ${baseResult.explain}`,
    };
  }
  if (daysLeft <= criticalDays) {
    return {
      score: scores.red,
      progress: baseResult.progress,
      achievement_ratio: baseResult.achievement_ratio,
      explain: `Critical — ${Math.ceil(daysLeft)}d until deadline ${opts.deadline_date}. ${baseResult.explain}`,
    };
  }
  if (daysLeft <= warningDays) {
    return {
      score: scores.yellow,
      progress: baseResult.progress,
      achievement_ratio: baseResult.achievement_ratio,
      explain: `Warning — ${Math.ceil(daysLeft)}d until deadline ${opts.deadline_date}. ${baseResult.explain}`,
    };
  }
  // Far from deadline — full marks. The whole point of this mode is to NOT
  // punish early-stage projects with low progress.
  return {
    score: scores.green,
    progress: baseResult.progress,
    achievement_ratio: baseResult.achievement_ratio,
    explain: `On track — deadline ${opts.deadline_date} (${Math.ceil(daysLeft)}d away). ${baseResult.explain}`,
  };
}

/**
 * Apply color_mode === 'pace' scoring (DEV-815).
 *
 * Color and score are derived from the variance between actual count and
 * the EXPECTED count by today, computed by linear interpolation between
 * tracking_start_date and deadline_date. Closes the gap between 'score'
 * mode (punishes early-stage projects) and 'deadline' mode (ignores pace).
 *
 * Worked example (Interns Hired): tracking_start April 1, deadline May 8,
 * target 6 → pace = 6/38 ≈ 0.158/day. On April 29 (28 days in), expected
 * is ~4.4 interns. Actual 5 → +0.6 variance → green. Actual 2 → −2.4
 * variance → red. Linear pace; non-linear curves are out of scope for v1.
 *
 * Tolerances are absolute units. Defaults from KPI_DEFAULTS; per-instance
 * overrides via pace_yellow_units_behind / pace_red_units_behind on the
 * SubKpiInstance.
 */
function applyColorModePaceScoring(
  baseResult: { score: number | null; progress: number | null; achievement_ratio: number | null; explain: string },
  goalMet: boolean,
  opts?: {
    deadline_date?: string | null;
    tracking_start_date?: string | null;
    asOfDate?: string;
    target_value?: number | null;
    current_value?: number | null;
    pace_yellow_units_behind?: number;
    pace_red_units_behind?: number;
  }
): { score: number | null; progress: number | null; achievement_ratio: number | null; explain: string } {
  const scores = KPI_DEFAULTS.mission.color_mode_deadline_scores;

  // Goal met → always green/100, regardless of pace.
  if (goalMet) {
    return {
      score: scores.green,
      progress: baseResult.progress, // DEV-864: progress passes through pace mode unchanged
      achievement_ratio: baseResult.achievement_ratio,
      explain: `Goal met. ${baseResult.explain}`,
    };
  }

  // Pace requires both endpoints AND a target. Without the period, we have
  // no pace to measure against; fall back to base score so the user still
  // sees something meaningful and can fix the missing config.
  const start = opts?.tracking_start_date;
  const end = opts?.deadline_date;
  const target = opts?.target_value;
  const current = opts?.current_value;

  if (!start || !end || target == null || current == null) {
    return {
      score: baseResult.score,
      progress: baseResult.progress,
      achievement_ratio: baseResult.achievement_ratio,
      explain: `Pace mode requires tracking start date, deadline, target, and current value. Falling back to base score. ${baseResult.explain}`,
    };
  }

  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  const asOfMs = opts.asOfDate ? new Date(opts.asOfDate).getTime() : Date.now();

  if (!(endMs > startMs)) {
    return {
      score: baseResult.score,
      progress: baseResult.progress,
      achievement_ratio: baseResult.achievement_ratio,
      explain: `Pace mode requires deadline after tracking start. Falling back to base score. ${baseResult.explain}`,
    };
  }

  // Pre-tracking-start → engine's tracking_start_date guard already handles
  // this by returning weight-0 tracked-only BEFORE we reach scoring. If we
  // somehow get here, return null.
  if (asOfMs < startMs) {
    return {
      score: null,
      progress: baseResult.progress,
      achievement_ratio: baseResult.achievement_ratio,
      explain: `Tracking begins ${start}.`,
    };
  }

  // Past deadline + not met → red (overdue is a hard signal in pace mode).
  if (asOfMs > endMs) {
    return {
      score: scores.overdue,
      progress: baseResult.progress,
      achievement_ratio: baseResult.achievement_ratio,
      explain: `Overdue — deadline was ${end} and goal not met. ${baseResult.explain}`,
    };
  }

  const daysInPeriod = (endMs - startMs) / 86_400_000;
  const daysElapsed = (asOfMs - startMs) / 86_400_000;
  const pacePerDay = target / daysInPeriod;
  const expected = pacePerDay * daysElapsed;
  const variance = current - expected; // positive = ahead, negative = behind

  const yellowBehind =
    typeof opts.pace_yellow_units_behind === 'number'
      ? opts.pace_yellow_units_behind
      : KPI_DEFAULTS.mission.pace_defaults.yellow_units_behind;
  const redBehind =
    typeof opts.pace_red_units_behind === 'number'
      ? opts.pace_red_units_behind
      : KPI_DEFAULTS.mission.pace_defaults.red_units_behind;

  const explain = `Expected ~${expected.toFixed(1)} by ${opts.asOfDate ?? 'today'}, actual ${current}. Variance ${variance >= 0 ? '+' : ''}${variance.toFixed(1)} units.`;

  // Variance ≥ −yellow → green (on or only slightly behind pace).
  if (variance >= -yellowBehind) {
    return { score: scores.green, progress: baseResult.progress, achievement_ratio: baseResult.achievement_ratio, explain: `On pace. ${explain}` };
  }
  // Variance < −yellow but ≥ −red → yellow.
  if (variance >= -redBehind) {
    return { score: scores.yellow, progress: baseResult.progress, achievement_ratio: baseResult.achievement_ratio, explain: `Behind pace. ${explain}` };
  }
  // Variance < −red → red.
  return { score: scores.red, progress: baseResult.progress, achievement_ratio: baseResult.achievement_ratio, explain: `Significantly behind pace. ${explain}` };
}

/**
 * Dispatch scoring based on value_type.
 *
 * All value types support optional deadline-based scoring.  Default behavior
 * (color_mode === 'score' or undefined) uses applyDeadlineScoring, which
 * caps the progress-based score as the deadline approaches.
 *
 * When color_mode === 'deadline', applyColorModeDeadlineScoring takes over
 * and the score becomes a discrete value derived from time-to-deadline.
 *
 * When color_mode === 'pace', applyColorModePaceScoring takes over and the
 * score reflects variance between actual and expected-by-today (DEV-815).
 */
function computeSubKpiScore(args: {
  value: number;
  target: TargetDef;
  cfgDirection?: MissionSubKpiConfig["direction"];
  value_type: MissionValueType;
  point?: MissionMetricPoint | null;
  /** Deadline + tolerance (all value types) */
  deadline_date?: string | null;
  warning_days?: number;
  critical_days?: number;
  asOfDate?: string;
  /** Color mode (DEV-814 / migration 122; 'pace' added DEV-815). Defaults vary by type. */
  color_mode?: 'score' | 'deadline' | 'pace';
  /** Pace-mode tolerances + lifecycle (DEV-815). Required only when color_mode='pace'. */
  tracking_start_date?: string | null;
  pace_yellow_units_behind?: number;
  pace_red_units_behind?: number;
}): { score: number | null; progress: number | null; achievement_ratio: number | null; explain: string } {
  const { value, target, cfgDirection, value_type, point, color_mode } = args;

  const deadlineOpts = {
    deadline_date: args.deadline_date,
    warning_days: args.warning_days,
    critical_days: args.critical_days,
    asOfDate: args.asOfDate,
  };

  // Resolve color_mode: explicit value > KPI_DEFAULTS per type.
  const effectiveColorMode: 'score' | 'deadline' | 'pace' =
    color_mode ??
    (KPI_DEFAULTS.mission.color_mode_defaults as Record<string, 'score' | 'deadline' | 'pace'>)[value_type] ??
    'score';

  /**
   * Compute base + goalMet for the given value_type, then route through the
   * appropriate color-mode handler. Pace mode needs extra args (target,
   * current_value, tracking_start_date) so it gets its own application
   * inside the switch instead of being selected ahead of time.
   */
  const applyMode = (
    base: { score: number | null; progress: number | null; achievement_ratio: number | null; explain: string },
    goalMet: boolean,
    currentValue: number,
    targetValue: number | null | undefined,
  ) => {
    if (effectiveColorMode === 'pace') {
      return applyColorModePaceScoring(base, goalMet, {
        deadline_date: args.deadline_date,
        tracking_start_date: args.tracking_start_date,
        asOfDate: args.asOfDate,
        target_value: targetValue,
        current_value: currentValue,
        pace_yellow_units_behind: args.pace_yellow_units_behind,
        pace_red_units_behind: args.pace_red_units_behind,
      });
    }
    if (effectiveColorMode === 'deadline') {
      return applyColorModeDeadlineScoring(base, goalMet, deadlineOpts);
    }
    return applyDeadlineScoring(base, goalMet, deadlineOpts);
  };

  switch (value_type) {
    case "binary":
      // Binary retains its existing self-contained deadline logic regardless
      // of color_mode (its scoreBinary already produces deadline-window values).
      return scoreBinary(value, deadlineOpts);

    case "percentage": {
      const base = scorePercentage(value, target);
      const goalMet = target.goal != null && target.goal > 0 && value >= target.goal;
      return applyMode(base, goalMet, value, target.goal);
    }

    case "milestone_set": {
      const base = scoreMilestoneSet(value, target, point);
      const total = target.milestone_total ?? target.goal ?? 0;
      const goalMet = total > 0 && value >= total;
      return applyMode(base, goalMet, value, total);
    }

    case "numeric":
    default: {
      const base = scoreNumeric({ value, target, cfgDirection });
      const direction = target.direction ?? cfgDirection ?? "increase_is_good";
      let goalMet = false;
      if (direction === "increase_is_good") {
        goalMet = target.goal != null && target.goal > 0 && value >= target.goal;
      } else if (direction === "decrease_is_good") {
        goalMet = target.goal != null && value <= target.goal;
      } else if (direction === "target_is_good") {
        goalMet = target.goal != null && value === target.goal;
      }
      return applyMode(base, goalMet, value, target.goal);
    }
  }
}

// ── Color / Trend ────────────────────────────────────────────

function colorValueFromScore(
  score: number | null,
  valueType?: MissionValueType,
): number {
  if (score === null || Number.isNaN(score)) return 0;

  // Binary's score IS its tolerance signal — values are deliberately discrete
  // (100 = on-track or achieved, 80 = warning window, 70 = critical window,
  // 0 = overdue or unachieved). Mapping with the universal 80/60 bands is
  // wrong: a score of 80 from the warning window would display green, hiding
  // the warning. Use Binary-specific bands instead.
  //
  // TEMPORARY PATCH (DEV-814): this duplicates logic also present in
  // `src/kpi-engine/missionTypes/binary.ts` colorFromScore. When the engine
  // migrates to dispatch via the missionTypes registry (DEV-814 Phase 4),
  // this branch and the universal logic below collapse — each type will
  // own its own color derivation through the registry.
  if (valueType === 'binary') {
    const levels = KPI_DEFAULTS.mission.binary_score_levels;
    if (score >= levels.achieved) return 3;       // green — achieved or on track
    if (score >= levels.warning_window) return 2; // yellow — warning window
    return 1;                                     // red — critical, overdue, or unachieved
  }

  // Universal score-band thresholds for all other value types.
  if (score >= KPI_DEFAULTS.mission.bands.green_threshold) return 3;
  if (score >= KPI_DEFAULTS.mission.bands.yellow_threshold) return 2;
  return 1;
}

function trendValueFromScoreDelta(delta: number | null): -1 | 0 | 1 {
  if (delta === null) return 0;
  if (delta > 0.5) return 1;
  if (delta < -0.5) return -1;
  return 0;
}

/**
 * Trend for cumulative value types (numeric cumulative, milestone_set).
 *
 * Uses the raw underlying value delta instead of the score delta so that
 * progress toward a goal is visible even when the score stays at 100
 * (deadline far away).  Any positive movement → arrow-up; any negative
 * movement → arrow-down; zero → flat.
 */
function trendValueFromRawDelta(
  latestValue: number | null,
  prevValue: number | null,
): -1 | 0 | 1 {
  if (latestValue === null || prevValue === null) return 0;
  const delta = latestValue - prevValue;
  if (delta > 0) return 1;
  if (delta < 0) return -1;
  return 0;
}

// ── Main Signal Computation ──────────────────────────────────

/**
 * Compute the Mission KPI signal for a project.
 *
 * @param missionPoints - All metric data points for this project
 * @param asOfDate - Evaluation date (YYYY-MM-DD)
 * @param config - Sub-KPI configuration (may be dynamic or from kpiConfig.ts)
 * @param projectTargets - Optional targets lookup. If omitted, falls back to
 *   the seed missionTargets file (backward compat).
 */
export function computeMissionKpiSignal(
  missionPoints: MissionMetricPoint[],
  asOfDate: string,
  config: MissionConfig,
  projectTargets?: Record<string, TargetDef>
): { signal: KpiSignal; subkpis: MissionSubKpiResult[] } {
  const project_GUID = missionPoints?.[0]?.project_GUID ?? "";

  // Use provided targets; no seed data fallback — empty means no scoring.
  const targetsForProject: Record<string, TargetDef> =
    projectTargets ?? {};

  // Group points by subKpiKey (case-insensitive for GUID-based keys)
  const pointsByKey: Record<string, MissionMetricPoint[]> = {};
  for (const p of missionPoints) {
    (pointsByKey[p.subKpiKey] ||= []).push(p);
  }
  // Also index by uppercase for case-insensitive GUID matching
  const pointsByKeyUpper: Record<string, MissionMetricPoint[]> = {};
  for (const [k, v] of Object.entries(pointsByKey)) {
    pointsByKeyUpper[k.toUpperCase()] = v;
  }
  for (const k of Object.keys(pointsByKey)) {
    pointsByKey[k].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  }


  const subkpisOut: MissionSubKpiResult[] = [];
  let weightedSum = 0;
  let weightTotal = 0;

  for (const subKpiKey of Object.keys(config.subkpis ?? {})) {
    const cfg = config.subkpis[subKpiKey];
    // Case-insensitive key match for GUIDs (exact first, then upper-case fallback)
    const series = pointsByKey[subKpiKey]
      ?? pointsByKeyUpper[subKpiKey.toUpperCase()]
      ?? [];
    const value_type: MissionValueType = cfg.value_type ?? "numeric";

    // Tracking start date guard (DEV-814): if the KPI hasn't started tracking
    // yet, contribute weight 0 and skip scoring. Avoids "early-stage projects
    // get 0% scores" complaint when the KPI was deliberately scheduled to
    // begin later in the project lifecycle.
    if (cfg.tracking_start_date && asOfDate < cfg.tracking_start_date) {
      subkpisOut.push({
        subKpiKey,
        label: cfg.label,
        score_0_100: null,
        progress_0_100: null, // DEV-864: tracked-only before tracking start
        achievement_ratio: null,
        latest_value: null,
        previous_value: null,
        latest_date: null,
        previous_date: null,
        explain: `Tracking begins ${cfg.tracking_start_date}.`,
        outcome_id: cfg.outcome_id,
        value_type,
        // DEV-963: weight_configured is required on MissionSubKpiResult. For
        // not-yet-tracking KPIs, weight_used is 0 but weight_configured reflects
        // the configured weight (still 0 contribution while tracked_only).
        weight_configured: cfg.weight ?? 0,
        weight_used: 0,
        tracked_only: true,
        indicator_color_value: 0,
        indicator_trend_value: 0,
        deadline_date: cfg.deadline_date,
        warning_days: cfg.warning_days,
        critical_days: cfg.critical_days,
        target_goal: cfg.target_goal,
        uom: cfg.uom,
        mission_type_config: cfg.mission_type_config,
        mission_type_key: cfg.mission_type_key, // DEV-822
      } as MissionSubKpiResult);
      continue;
    }

    // ── DEV-1075 Path A — qualitative scoring branch (dual-track) ──
    //
    // When scoring_mode='qualitative' (Mini-Project tier), color and
    // score come from the latest .assessment via the 5-state ladder
    // (qualitativeLadder.ts), NOT from value-vs-target band thresholds.
    // The numeric / deadline / band pipeline below is bypassed.
    //
    // Dual-track (Path A — DEV-1075 Phase 5.3): the qualitative ladder
    // drives score_0_100 + indicator_color_value (the Lead's judgment
    // of "are we on track?"), but progress_0_100 + latest_value come
    // from the most recent NUMERIC point so the progress bar tells the
    // truth about completion. Per goal primitive:
    //   - milestone_set: count of items with latest value >= 1 ÷
    //                    target_goal (or item count) × 100
    //   - percentage:    latest numeric value (already 0-100)
    //   - count/currency/numeric: latest value / target_goal × 100,
    //                    clamped at 100
    //   - no value-bearing point yet → fall back to qScore so the bar
    //                    isn't empty when the Lead has assessed but not
    //                    entered a number
    //
    // included_in_rollup=false suppresses contribution to the
    // weighted-average Mission score but the card's own indicator is
    // still computed and rendered.
    if (cfg.scoring_mode === 'qualitative') {
      const latestQ = asOfPoint(series, asOfDate, (p) => p.date);
      const prevQ = latestQ
        ? previousPoint(series, latestQ.date, (p) => p.date)
        : null;

      const rawLatestA = latestQ?.assessment;
      const rawPrevA = prevQ?.assessment;
      const latestAssessment: QualitativeAssessment | null =
        isQualitativeAssessment(rawLatestA) ? rawLatestA : null;
      const prevAssessment: QualitativeAssessment | null =
        isQualitativeAssessment(rawPrevA) ? rawPrevA : null;

      const qScore = latestAssessment !== null
        ? assessmentToScore(latestAssessment)
        : null;
      const qPrevScore = prevAssessment !== null
        ? assessmentToScore(prevAssessment)
        : null;
      const qColorVal: 0 | 1 | 2 | 3 = latestAssessment !== null
        ? assessmentToColorValue(latestAssessment)
        : 0;

      const qTrendDelta =
        qScore !== null && qPrevScore !== null ? (qScore - qPrevScore) : null;
      const qTrendValue: -1 | 0 | 1 = trendValueFromScoreDelta(qTrendDelta);

      const qTrackedOnly = qScore === null;
      const qInRollup = cfg.included_in_rollup !== false;
      const qWeightUsed = (qTrackedOnly || !qInRollup) ? 0 : (cfg.weight ?? 0);

      if (qWeightUsed > 0 && qScore !== null) {
        weightedSum += qWeightUsed * qScore;
        weightTotal += qWeightUsed;
      }

      // ── Dual-track: derive progress + latest_value from numeric data ──
      // Separate from the qualitative score/color derivation above.
      let qProgress: number | null = null;
      let qLatestValue: number | null = null;

      if (value_type === 'milestone_set') {
        // Per-item derivation: count items with latest value >= 1.
        // Same algorithm as the DEV-816 numeric milestone_set path; we
        // run it locally here so the qualitative branch can compute its
        // own progress without sharing state with the numeric pipeline.
        const itemRows = series.filter(
          (p) => p.item_id != null && p.date <= asOfDate,
        );
        if (itemRows.length > 0) {
          const byItem = new Map<string, MissionMetricPoint>();
          for (const row of itemRows) {
            const prior = byItem.get(row.item_id!);
            if (!prior || row.date > prior.date) byItem.set(row.item_id!, row);
          }
          let completedNow = 0;
          for (const row of byItem.values()) {
            if (row.value >= 1) completedNow++;
          }
          // Total: prefer cfg.target_goal (explicit checklist size) and
          // fall back to the number of distinct items seen (caps at 100%
          // for partially-defined checklists).
          const total = (cfg.target_goal && cfg.target_goal > 0)
            ? cfg.target_goal
            : Math.max(1, byItem.size);
          qLatestValue = completedNow;
          qProgress = clamp01(completedNow / total) * 100;
        }
      } else {
        // Non-checklist: find the most recent point that actually carries
        // a numeric value (assessment-only points have value=0 — see the
        // metricPointToSeedShape coercion in api/missionMetrics.ts).
        // We look for a value > 0 OR an explicit zero accompanied by no
        // assessment, to avoid mistaking an assessment-only row for a
        // "value=0 snapshot."
        const valueBearing = series.filter(
          (p) => p.date <= asOfDate && (p.value !== 0 || !p.assessment),
        );
        const latestValuePoint = valueBearing.length > 0
          ? valueBearing.reduce((best, p) =>
              !best || p.date > best.date ? p : best,
              null as MissionMetricPoint | null)
          : null;

        if (latestValuePoint !== null) {
          qLatestValue = latestValuePoint.value;
          if (value_type === 'percentage') {
            // Value is already on the 0-100 scale.
            qProgress = clamp01(latestValuePoint.value / 100) * 100;
          } else if (cfg.target_goal && cfg.target_goal > 0) {
            qProgress = clamp01(latestValuePoint.value / cfg.target_goal) * 100;
          }
          // No target_goal → no meaningful progress ratio; fall through.
        }
      }

      // Fallback when no value-bearing point exists yet (goal has been
      // assessed but no numeric snapshot or per-item completion recorded).
      // Mirror the qualitative score so the progress bar isn't empty.
      if (qProgress === null) {
        qProgress = qScore;
        qLatestValue = latestQ?.value ?? null;
      }

      subkpisOut.push({
        subKpiKey,
        label: cfg.label || undefined,
        score_0_100: qScore,
        progress_0_100: qProgress,
        achievement_ratio: qProgress,
        indicator_color_value: qColorVal,
        indicator_trend_value: qTrendValue,
        weight_used: qWeightUsed,
        weight_configured: cfg.weight ?? 0,
        tracked_only: qTrackedOnly,
        latest_date: latestQ?.date ?? null,
        // Dual-track: latest_value is the derived numeric (count-done
        // for checklist, raw value otherwise), not the assessment row's
        // null value. This is what the progress bar and "X of Y" labels
        // consume.
        latest_value: qLatestValue,
        explain: latestAssessment !== null
          ? `Qualitative assessment: ${latestAssessment}.`
          : 'Qualitative: no assessment recorded yet.',
        value_type,
        outcome_id: cfg.outcome_id,
        tracking_start_date: cfg.tracking_start_date ?? null,
        deadline_date: cfg.deadline_date ?? null,
        warning_days: cfg.warning_days,
        critical_days: cfg.critical_days,
        uom: cfg.uom,
        target_goal: cfg.target_goal ?? null,
        tags: cfg.tags,
        mission_type_config: cfg.mission_type_config,
        mission_type_key: cfg.mission_type_key,
      } as MissionSubKpiResult);
      continue;
    }

    // DEV-816 Option D / Skip: Checklist (milestone_set) per-item handling.
    //
    // When the data series contains item_id-bearing rows, derive the count
    // from those rows directly rather than treating each row as a periodic
    // count snapshot. Each item_id is a separate completion event; we take
    // the latest value per item_id (as of asOfDate) and count items whose
    // latest value is >= 1.
    //
    // Synthesizes a virtual "latest point" with value = derived count so
    // the rest of the scoring pipeline (deadline windows, color modes,
    // band thresholds) works without further branching.
    //
    // Falls back to legacy behavior when no item_id rows exist (every row
    // has item_id = null) — preserves existing data + first-N rendering.
    let workingSeries = series;
    if (value_type === "milestone_set") {
      const itemRows = series.filter((p) => p.item_id != null && p.date <= asOfDate);
      if (itemRows.length > 0) {
        // Group by item_id, take latest row per item, ALSO track event
        // dates so we can synthesize a "before most-recent-event" snapshot
        // for trend computation. (DEV-816 / Skip: trend was always flat
        // because we were synthesizing only one point.)
        const byItem = new Map<string, MissionMetricPoint>();
        for (const row of itemRows) {
          const prior = byItem.get(row.item_id!);
          if (!prior || row.date > prior.date) byItem.set(row.item_id!, row);
        }
        // Count "now" — items with latest value >= 1.
        let completedNow = 0;
        for (const row of byItem.values()) {
          if (row.value >= 1) completedNow++;
        }

        // Find the most-recent event date across all item rows.
        const sortedRows = [...itemRows].sort((a, b) => a.date.localeCompare(b.date));
        const mostRecentDate = sortedRows[sortedRows.length - 1].date;

        // Count "before most-recent event" — items with latest value >= 1
        // looking only at rows STRICTLY EARLIER than mostRecentDate.
        const earlierRows = itemRows.filter(r => r.date < mostRecentDate);
        const byItemBefore = new Map<string, MissionMetricPoint>();
        for (const row of earlierRows) {
          const prior = byItemBefore.get(row.item_id!);
          if (!prior || row.date > prior.date) byItemBefore.set(row.item_id!, row);
        }
        let completedBefore = 0;
        for (const row of byItemBefore.values()) {
          if (row.value >= 1) completedBefore++;
        }

        // Synthesize TWO virtual points so trend computation has a prev
        // to compare against. Earlier point gets a synthetic earlier date
        // (1ms before the most-recent event); later point sits at the
        // most-recent event date.
        const proj = series[0]?.project_GUID ?? "";
        const earlierSynthDate = mostRecentDate; // ordering-only marker
        workingSeries = [
          {
            project_GUID: proj,
            subKpiKey,
            date: earlierSynthDate < mostRecentDate ? earlierSynthDate : '0000-01-01',
            value: completedBefore,
          },
          {
            project_GUID: proj,
            subKpiKey,
            date: mostRecentDate,
            value: completedNow,
          },
        ];
      }
    }

    const latest = asOfPoint(workingSeries, asOfDate, (p) => p.date);
    const prev = latest
      ? previousPoint(workingSeries, latest.date, (p) => p.date)
      : null;

    // Case-insensitive target lookup
    const target: TargetDef = targetsForProject?.[subKpiKey]
      ?? (Object.entries(targetsForProject ?? {}).find(
        ([k]) => k.toUpperCase() === subKpiKey.toUpperCase()
      )?.[1] as TargetDef)
      ?? {};

    const latestEval = latest
      ? computeSubKpiScore({
          value: latest.value,
          target,
          cfgDirection: cfg.direction,
          value_type,
          point: latest,
          deadline_date: cfg.deadline_date,
          warning_days: cfg.warning_days,
          critical_days: cfg.critical_days,
          asOfDate,
          color_mode: cfg.color_mode,
          tracking_start_date: cfg.tracking_start_date,
          pace_yellow_units_behind: cfg.pace_yellow_units_behind,
          pace_red_units_behind: cfg.pace_red_units_behind,
        })
      : cfg.deadline_date
        // Deadline configured but no data yet — still score based on deadline proximity.
        // Applies to all value types (binary, numeric, percentage, milestone_set).
        //
        // DEV-816 / Skip: past-deadline with no data ever recorded is a hard
        // failure signal, not a "we don't know yet" state. Promote non-Binary
        // types to score=0 (red) when past deadline so the indicator matches
        // user intuition. Binary already handles this via its base score=0;
        // non-Binary used to return null (grey) which understated the problem.
        // For deadlines still in the FUTURE we keep score=null (tracked-only,
        // grey) — that genuinely is "no data yet, not late."
        ? applyDeadlineScoring(
            value_type === "binary"
              // DEV-864: no-data branches have null progress (no value to ratio against target).
              ? { score: 0, progress: null, achievement_ratio: 0, explain: "Achievement not yet reached." }
              : (asOfDate > cfg.deadline_date)
                ? { score: 0, progress: null, achievement_ratio: 0, explain: "Past deadline with no data recorded." }
                : { score: null, progress: null, achievement_ratio: null, explain: "No observations as-of date." },
            false, // goal not met (no data)
            {
              deadline_date: cfg.deadline_date,
              warning_days: cfg.warning_days,
              critical_days: cfg.critical_days,
              asOfDate,
            }
          )
        : { score: null, progress: null, achievement_ratio: null, explain: "No observations as-of date." };

    const prevEval = prev
      ? computeSubKpiScore({
          value: prev.value,
          target,
          cfgDirection: cfg.direction,
          value_type,
          point: prev,
          deadline_date: cfg.deadline_date,
          warning_days: cfg.warning_days,
          critical_days: cfg.critical_days,
          asOfDate: prev.date,
          color_mode: cfg.color_mode,
          tracking_start_date: cfg.tracking_start_date,
          pace_yellow_units_behind: cfg.pace_yellow_units_behind,
          pace_red_units_behind: cfg.pace_red_units_behind,
        })
      : { score: null, progress: null, achievement_ratio: null, explain: "No prior observation." };

    // Determine trend direction.
    // DEV-816 / Skip: expanded value-based trend coverage.
    //
    // ORIGINAL behavior: only cumulative numeric and milestone_set used
    // raw value delta; everything else used score delta. Three real bugs
    // surfaced from that:
    //   - Rate (percentage): score caps at 100 once value ≥ target, so
    //     subsequent value movements don't move the score → arrow stayed
    //     flat. Skip's report.
    //   - Binary: a single achievement entry has no prev → score delta
    //     null → arrow stayed flat (circle) even though "0 → achieved" is
    //     a meaningful upward event.
    //   - Checklist (milestone_set, Option D): synthesizes one virtual
    //     point from item_id rows → prev is always null → arrow flat.
    //
    // Fix: percentage and binary join the value-based trend group. For
    // types where the FIRST observation carries implicit upward meaning
    // (Binary achievement, Checklist completion), null prev is treated
    // as 0 so first-record-with-value registers as an up-arrow.
    //
    // Reduction stays on score-based trend deliberately — its score
    // formula is naturally responsive to value changes (no cap), and
    // score-direction aligns with "is this getting better" intuition for
    // a decrease-is-good metric.
    const usesValueBasedTrend =
      value_type === "binary" ||
      value_type === "percentage" ||
      value_type === "milestone_set" ||
      (value_type === "numeric" && cfg.aggregation === "cumulative");

    const treatNullPrevAsZero =
      value_type === "binary" || value_type === "milestone_set";

    const prevValForTrend =
      prev?.value == null
        ? (treatNullPrevAsZero ? 0 : null)
        : prev.value;

    const trendValue: -1 | 0 | 1 = usesValueBasedTrend
      ? trendValueFromRawDelta(latest?.value ?? null, prevValForTrend)
      : trendValueFromScoreDelta(
          (latestEval.score !== null && prevEval.score !== null)
            ? (latestEval.score - prevEval.score)
            : null
        );

    const tracked_only = latestEval.score === null;
    // DEV-1075. included_in_rollup=false suppresses contribution to the
    // Mission weighted average. The card's own indicator color is still
    // computed (rendered below); only the rollup math excludes the row.
    // Replaces the weight=0 workaround for administrative sub-KPIs.
    const inRollup = cfg.included_in_rollup !== false;
    const weight_used = (tracked_only || !inRollup) ? 0 : (cfg.weight ?? 0);

    if (!tracked_only && weight_used > 0) {
      weightedSum += weight_used * (latestEval.score ?? 0);
      weightTotal += weight_used;
    }

    subkpisOut.push({
      subKpiKey,
      label: cfg.label || undefined,
      score_0_100: latestEval.score,
      // DEV-864: progress_0_100 carries the raw % toward target, no deadline penalty.
      // Drives the MissionSubKpiCard progress bar fill.
      progress_0_100: latestEval.progress,
      achievement_ratio: latestEval.achievement_ratio,
      indicator_color_value: colorValueFromScore(latestEval.score, value_type),
      indicator_trend_value: trendValue,
      weight_used,
      weight_configured: cfg.weight ?? 0,
      tracked_only,
      latest_date: latest?.date ?? null,
      latest_value: latest?.value ?? null,
      explain: latestEval.explain,
      value_type,
      outcome_id: cfg.outcome_id,
      tracking_start_date: cfg.tracking_start_date ?? null,
      deadline_date: cfg.deadline_date ?? null,
      warning_days: cfg.warning_days,
      critical_days: cfg.critical_days,
      uom: cfg.uom,
      target_goal: target?.goal ?? cfg.target_goal ?? null,
      tags: cfg.tags,
      mission_type_config: cfg.mission_type_config,
      mission_type_key: cfg.mission_type_key, // DEV-822
    });
  }

  const missionScore = weightTotal > 0 ? (weightedSum / weightTotal) : null;

  // Composite trend: weighted vote from scored sub-KPIs.
  // Each sub-KPI's trend (-1, 0, +1) is multiplied by its weight.
  // Net positive → up, net negative → down, zero → flat.
  let weightedTrendSum = 0;
  for (const sub of subkpisOut) {
    if (!sub.tracked_only && sub.weight_used > 0) {
      weightedTrendSum += sub.weight_used * sub.indicator_trend_value;
    }
  }
  const compositeTrend: -1 | 0 | 1 =
    weightedTrendSum > 0 ? 1 : weightedTrendSum < 0 ? -1 : 0;

  const signal: KpiSignal = {
    score_0_100: missionScore,
    indicator_color_value: colorValueFromScore(missionScore),
    indicator_trend_value: compositeTrend,
    as_of_date: asOfDate,
    explain: {
      headline: missionScore === null
        ? "Mission not scored (no scored SubKPIs)."
        : `Mission score ${missionScore.toFixed(1)} (weighted).`,
      details: {
        project_GUID,
        weight_total: weightTotal,
      },
    },
  };

  return { signal, subkpis: subkpisOut };
}
